import { Assert } from "../../Assert";
import { Constructor, PartialNullable } from "../../Extensions";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32, PropertyObject, PropertyString } from "../../RadObject";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { ControlConstructor, IControl } from "./Control";
import { IEntitySearch } from "./EntitySearch";
import { IEntitySearchInfiniteScroll } from "./EntitySearchInfiniteScroll";
import { EntitySearchSortDirectionEnum } from "./EntitySearchSortDirectionEnum";
import { IInputControl } from "./InputControl";
import { TableCellEntitySearch } from "./TableCellEntitySearch";
import { ITableColumn, TableColumn, TableColumnPropertyValues } from "./TableColumn";
import { NAMESPACE } from "./_Namespace";

export type TableColumnEntitySearchPropertyValues =
    TableColumnPropertyValues
    & {
        applicationEntitySupportsSoftDelete: boolean;
        createEditorControl: () => IInputControl;
        createEditorControlProperties: () => any;
        createViewControl: () => IControl;
        createViewControlProperties: () => any;
        editorControl: IInputControl;
        editorControlClass: Constructor<IInputControl>;
        editorControlProperties: any;
        hideTitle: boolean;
        index: number;
        manual: boolean;
        sortable: boolean;
        sortProperty: string;
        viewControl: IControl;
        viewControlClass: Constructor<IControl>;
        viewControlProperties: any;
    };

export type TableColumnEntitySearchProperties = {
    [name in keyof TableColumnEntitySearchPropertyValues]: Property<TableColumnEntitySearchPropertyValues[name]>
};

export interface ITableColumnEntitySearch extends ITableColumn, TableColumnEntitySearchProperties
{
}

export type TableColumnEntitySearchConstructor = ControlConstructor & {
    getRequiredFields(): string[];
}

export class TableColumnEntitySearch extends TableColumn implements ITableColumnEntitySearch
{
    // #region Static

    public static override classNamespace = NAMESPACE + "TableColumnEntitySearch";
    public static override className = "TableColumnEntitySearch";

    // #region Public Methods

    public static getRequiredFields(): string[]
    {
        return [];
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableColumnEntitySearchPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                applicationEntitySupportsSoftDelete: false,
                manual: false,
                sortable: true,
                tableCellClass: TableCellEntitySearch,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _linkElementRef?: ElementRef;
    protected _sortDirectionElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public applicationEntitySupportsSoftDelete!: PropertyBoolean;
    public createEditorControl!: Property<() => IInputControl>;
    public createEditorControlProperties!: Property<() => any>;
    public createViewControl!: Property<() => IControl>;
    public createViewControlProperties!: Property<() => any>;
    public editorControl!: Property<IInputControl>;
    public editorControlClass!: Property<Constructor<IInputControl>>;
    public editorControlProperties!: PropertyObject;
    public hideTitle!: PropertyBoolean;
    public index!: PropertyInt32;
    public manual!: PropertyBoolean;
    public sortable!: PropertyBoolean;
    public sortProperty!: PropertyString;
    public viewControl!: Property<IControl>;
    public viewControlClass!: Property<Constructor<IControl>>;
    public viewControlProperties!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableColumnEntitySearchPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-column-entity-search");

        // Trigger Property Changes
        this.hideTitle.triggerChange();
        this.sortable.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _applicationEntitySupportsSoftDeleteChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _editorControlChanged(change: PropertyChange<IInputControl>)
    {
        /* Intentionally empty */
    }
    protected _editorControlClassChanged(change: PropertyChange<Constructor<IInputControl>>)
    {
        /* Intentionally empty */
    }
    protected _editorControlPropertiesChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _getEntitySearch(): IEntitySearch | IEntitySearchInfiniteScroll | undefined
    {
        const
            entitySearch = this.$$element.get()?.closest(".rad-control-entity-search").radControl() as IEntitySearch | undefined;

        if (entitySearch)
            return entitySearch;

        const
            entitySearchInfiniteScroll = this.$$element.get()?.closest(".rad-control-entity-search-infinite-scroll").radControl() as IEntitySearchInfiniteScroll | undefined;

        if (entitySearchInfiniteScroll)
            return entitySearchInfiniteScroll;
    }
    protected _hideTitleChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this._containerElementRef.current.hide();
        else
            this._containerElementRef.current.show();
    }
    protected _manualChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _sortableChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _sortPropertyChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _updateDisplay()
    {
        const
            entitySearch = this._getEntitySearch();

        if (!entitySearch)
            return;

        Assert.isNotNullOrUndefined(this._sortDirectionElementRef, "this._sortDirectionElementRef");

        this._sortDirectionElementRef.current.removeClass("up");
        this._sortDirectionElementRef.current.removeClass("down");
        this._sortDirectionElementRef.current.addClass("rad-state-hidden");

        if (this.manual.get() && !this.sortProperty.get())
            return;

        if (entitySearch.sortByOrderProperty.get())
            return;

        if ((entitySearch as IEntitySearch).defaultSortListProperty)
        {
            if ((entitySearch as IEntitySearch).defaultSortListProperty.get() === this.dataSourceProperty.get())
            {
                this._sortDirectionElementRef.current.addClass(
                    entitySearch.defaultSortListPropertyDirection.get() === EntitySearchSortDirectionEnum.descending
                        ? "down"
                        : "up"
                );
                this._sortDirectionElementRef.current.removeClass("rad-state-hidden");
            }
        }

        const
            $$element = this.$$element.get(),
            columnIndex = $$element ? $$element.parent().children().index($$element) : -1;

        if ((entitySearch as IEntitySearchInfiniteScroll).defaultSortListPropertyIndex)
        {
            if ((entitySearch as IEntitySearchInfiniteScroll).defaultSortListPropertyIndex.get() === columnIndex)
            {
                this._sortDirectionElementRef.current.addClass(
                    entitySearch.defaultSortListPropertyDirection.get() === EntitySearchSortDirectionEnum.descending
                        ? "down"
                        : "up"
                );
                this._sortDirectionElementRef.current.removeClass("rad-state-hidden");
            }
        }
    }
    protected _viewControlChanged(change: PropertyChange<IControl>)
    {
        /* Intentionally empty */
    }
    protected _viewControlClassChanged(change: PropertyChange<Constructor<IControl>>)
    {
        /* Intentionally empty */
    }
    protected _viewControlPropertiesChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TableColumnEntitySearchPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._sortDirectionElementRef = new ElementRef();

        this.applicationEntitySupportsSoftDelete = new PropertyBoolean(this, propertyValues.applicationEntitySupportsSoftDelete, this._applicationEntitySupportsSoftDeleteChanged);
        this.createEditorControl = new Property<() => IInputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.createEditorControl);
        this.createEditorControlProperties = new Property<() => any>(this, "System.Object, System.Private.CoreLib", propertyValues.createEditorControlProperties);
        this.createViewControl = new Property<() => IControl>(this, "System.Object, System.Private.CoreLib", propertyValues.createViewControl);
        this.createViewControlProperties = new Property<() => any>(this, "System.Object, System.Private.CoreLib", propertyValues.createViewControlProperties);
        this.editorControl = new Property<IInputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.editorControl, this._editorControlChanged);
        this.editorControlClass = new Property<Constructor<IInputControl>>(this, "System.Object, System.Private.CoreLib", propertyValues.editorControlClass, this._editorControlClassChanged);
        this.editorControlProperties = new PropertyObject(this, propertyValues.editorControlProperties, this._editorControlPropertiesChanged);
        this.hideTitle = new PropertyBoolean(this, propertyValues.hideTitle, this._hideTitleChanged);
        this.index = new PropertyInt32(this, propertyValues.index);
        this.manual = new PropertyBoolean(this, propertyValues.manual, this._manualChanged);
        this.sortable = new PropertyBoolean(this, propertyValues.sortable, this._sortableChanged);
        this.sortProperty = new PropertyString(this, propertyValues.sortProperty, this._sortPropertyChanged);
        this.viewControl = new Property<IControl>(this, "System.Object, System.Private.CoreLib", propertyValues.viewControl, this._viewControlChanged);
        this.viewControlClass = new Property<Constructor<IControl>>(this, "System.Object, System.Private.CoreLib", propertyValues.viewControlClass, this._viewControlClassChanged);
        this.viewControlProperties = new PropertyObject(this, propertyValues.viewControlProperties, this._viewControlPropertiesChanged);

        this.editorControl.overrideGet(next =>
        {
            let
                editorControl = next();

            if (!editorControl)
            {
                const
                    createEditorControl = this.createEditorControl.get();

                if (createEditorControl)
                {
                    editorControl = createEditorControl();
                    this.editorControl.set(editorControl);
                }
            }

            return editorControl;
        });
        this.editorControlProperties.overrideGet(next =>
        {
            let
                editorControlProperties = next();

            if (!editorControlProperties)
            {
                const
                    createEditorControlProperties = this.createEditorControlProperties.get();

                if (createEditorControlProperties)
                {
                    editorControlProperties = createEditorControlProperties();
                    this.editorControlProperties.set(editorControlProperties);
                }
            }

            return editorControlProperties;
        });
        this.viewControl.overrideGet(next =>
        {
            let
                viewControl = next();

            if (!viewControl)
            {
                const
                    createViewControl = this.createViewControl.get();

                if (createViewControl)
                {
                    viewControl = createViewControl();
                    this.viewControl.set(viewControl);
                }
            }

            return viewControl;
        });
        this.viewControlProperties.overrideGet(next =>
        {
            let
                viewControlProperties = next();

            if (!viewControlProperties)
            {
                const
                    createViewControlProperties = this.createViewControlProperties.get();

                if (createViewControlProperties)
                {
                    viewControlProperties = createViewControlProperties();
                    this.viewControlProperties.set(viewControlProperties);
                }
            }

            return viewControlProperties;
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this._linkElementRef?.current.on("click", { self: this }, this.onLinkElementClicked);
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        const
            sortable = !(!this.sortable.get() || this.manual.get() && !this.sortProperty.get()),
            containerFragment =
                sortable
                    ? <a ref={this._containerElementRef} href="#">{children ?? this._createChildrenHtml()}</a>
                    : <span ref={this._containerElementRef}>{children ?? this._createChildrenHtml()}</span>;

        if (sortable)
            this._linkElementRef = this._containerElementRef;

        return (
            <th>
                {containerFragment}
                <i ref={this._sortDirectionElementRef} class="angle up icon rad-state-hidden"></i>
            </th>
        );
    }

    // #endregion

    // #region Event Handlers

    protected onLinkElementClicked(evt: IJNativeEvent)
    {
        evt.preventDefault();
        evt.stopPropagation();

        const
            self = evt.data.self as this,
            entitySearch = self._getEntitySearch();

        if (!entitySearch)
            return;

        if ((entitySearch as any)._setSortListProperty)
        {
            (entitySearch as IEntitySearch)._setSortListProperty(self.dataSourceProperty.get() ?? undefined);
            return;
        }

        if ((entitySearch as any)._setSortListPropertyIndex)
        {
            const
                tableColumn = self.$$element.get()?.closest("th").radControl() as ITableColumnEntitySearch | undefined;

            (entitySearch as IEntitySearchInfiniteScroll)._setSortListPropertyIndex(tableColumn?.index.get() ?? undefined);
            
            return;
        }
    }

    // #endregion

    // #endregion
}